import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import {
  Cake, ArrowRight, Users, ShieldCheck, MapPin, Sparkles, Heart,
} from "lucide-react";

const SLIDES = [
  {
    key: "welcome",
    icon: Cake,
    iconBg: "from-sky-400 to-sky-500",
    title: (name) => `Welcome, ${name}!`,
    body: "You just unlocked access to hundreds of birthday rewards — from coffee and desserts to full meals and retail perks. Some are completely free, others need a small purchase or signup. We'll always tell you upfront.",
  },
  {
    key: "mission",
    icon: Heart,
    iconBg: "from-rose-400 to-rose-500",
    title: () => "This Isn't a Coupon App",
    body: "Every reward in here is a verified birthday offer from a real business. We research, track, and update them so you don't have to dig through fine print or expired links.",
  },
  {
    key: "community",
    icon: Users,
    iconBg: "from-amber-400 to-orange-500",
    title: () => "Powered by People Like You",
    body: "When you claim a deal, let us know if it worked. Your quick verification helps thousands of other birthday celebrants get the real deal — and earns you 15 pts each on our leaderboard.",
  },
  {
    key: "california",
    icon: MapPin,
    iconBg: "from-emerald-400 to-emerald-500",
    title: () => "California First",
    body: "We're launching with a limited California rollout to make sure every deal is accurate and every feature is solid. More states are coming soon — you're part of the founding crew.",
  },
  {
    key: "trust",
    icon: ShieldCheck,
    iconBg: "from-violet-400 to-violet-500",
    title: () => "Verified. Updated. Honest.",
    body: "Every offer shows a confidence score, last-verified date, and any fine print so you know exactly what you're walking into. No surprises, no bait-and-switch.",
  },
];

export default function WelcomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const slide = SLIDES[idx];
  const isLast = idx === SLIDES.length - 1;
  const firstName = user?.first_name || "there";

  const handleNext = () => {
    if (isLast) {
      localStorage.setItem("has_seen_welcome", "true");
      navigate("/disclaimer", { replace: true });
    } else {
      setIdx(idx + 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem("has_seen_welcome", "true");
    navigate("/disclaimer", { replace: true });
  };

  const Icon = slide.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex flex-col">
      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 pt-10 pb-4">
        {SLIDES.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === idx ? "w-8 bg-sky-500" : i < idx ? "w-6 bg-sky-300" : "w-6 bg-slate-200"
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.key}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-md text-center"
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br ${slide.iconBg} shadow-xl`}
              >
                <Icon className="w-10 h-10 text-white" />
              </div>
            </div>

            {/* Title */}
            <h1
              className="text-2xl sm:text-3xl font-bold text-slate-800 mb-4 leading-snug"
              data-testid={`welcome-title-${slide.key}`}
            >
              {slide.title(firstName)}
            </h1>

            {/* Body */}
            <p
              className="text-base text-slate-500 leading-relaxed max-w-sm mx-auto"
              data-testid={`welcome-body-${slide.key}`}
            >
              {slide.body}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom actions */}
      <div className="px-6 pb-10 space-y-3 w-full max-w-md mx-auto">
        <button
          onClick={handleNext}
          className="w-full py-4 rounded-full bg-gradient-to-r from-sky-400 to-sky-500 text-white font-semibold shadow-lg shadow-sky-500/30 hover:shadow-sky-500/50 transition-all flex items-center justify-center gap-2"
          data-testid="welcome-next-btn"
        >
          {isLast ? (
            <>
              <Sparkles className="w-5 h-5" />
              Let's Go — Show Me My Deals
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
